import { computed, useHost, watchEffect, shallowRef } from "vue";
import { closestAcrossShadowBoundaries, FORM_ASSOCIATED_ELEMENTS } from "@/runtime/shared";
import { mergeClassProp } from "@/runtime/style-engine/define-variants";
import type { LabelProps } from "./label.types";
import { labelClasses } from "./label.styles";

export interface UseLabelOptions {
  props: Readonly<LabelProps>;
}

/** Interactive element tags that should not trigger label forwarding. */
const INTERACTIVE_TAGS = new Set(["BUTTON", "INPUT", "A"]);

/**
 * Check if parent component (radio, checkbox, etc.) or parent group is disabled
 */
const checkParentDisabled = (host: Element | null): boolean => {
  if (!host) return false;

  const parent = closestAcrossShadowBoundaries(host, FORM_ASSOCIATED_ELEMENTS);
  if (!parent) return false;

  if (parent.hasAttribute("disabled") && parent.getAttribute("disabled") !== "false") {
    return true;
  }

  const group = closestAcrossShadowBoundaries(parent, "e-radio-group, e-checkbox-group");
  return !!(group?.hasAttribute("disabled") && group.getAttribute("disabled") !== "false");
};

/**
 * Find the associated element by `for` prop ID.
 */
const findAssociatedElement = (forId: string | undefined, host: Element | null): HTMLElement | null => {
  if (!forId || !host) return null;

  const selector = `#${CSS.escape(forId)}`;
  return (
    document.querySelector<HTMLElement>(selector) ||
    (host.getRootNode() as Document | ShadowRoot).querySelector<HTMLElement>(selector) ||
    null
  );
};

/**
 * Check if the associated element is disabled.
 */
const checkAssociatedDisabled = (element: HTMLElement | null): boolean => {
  if (!element) return false;

  if ("disabled" in element && (element as any).disabled === true) return true;

  if (element.hasAttribute("disabled")) {
    const attr = element.getAttribute("disabled");
    return attr !== "false" && attr !== null;
  }

  return false;
};

/**
 * Trigger the associated element (handles both native and custom elements).
 */
const triggerElement = (element: HTMLElement, forId: string | undefined, host: Element | null): void => {
  if (checkAssociatedDisabled(findAssociatedElement(forId, host))) return;

  if (element.shadowRoot) {
    const interactive = element.shadowRoot.querySelector<HTMLElement>(
      'button, input, textarea, select, [role="button"], [role="checkbox"], [role="radio"]',
    );
    if (interactive) {
      interactive.click();
      interactive.focus?.();
      return;
    }
  }

  element.click();
  if ("focus" in element && typeof element.focus === "function") {
    element.focus();
  }
};

/** Returns true if the click target is inside an interactive element. */
const isInteractiveTarget = (target: HTMLElement): boolean => {
  return (
    INTERACTIVE_TAGS.has(target.tagName) ||
    !!target.closest("button") ||
    !!target.closest("input") ||
    !!target.closest("a")
  );
};

/**
 * Observes a single element's "disabled" attribute and calls `update` on change.
 * Returns the observer for cleanup.
 */
const observeDisabled = (el: Element, update: () => void): MutationObserver => {
  const observer = new MutationObserver(update);
  observer.observe(el, { attributes: true, attributeFilter: ["disabled"] });
  return observer;
};

/**
 * Sets up disabled-state watching for the `for` prop scenario:
 * observe the associated element's disabled attribute.
 */
const watchForProp = (
  forId: string,
  host: Element | null,
  isDisabled: { value: boolean },
  onCleanup: (fn: () => void) => void,
): void => {
  isDisabled.value = checkAssociatedDisabled(findAssociatedElement(forId, host));

  const element = findAssociatedElement(forId, host);
  if (!element) return;

  const observer = observeDisabled(element, () => {
    isDisabled.value = checkAssociatedDisabled(findAssociatedElement(forId, host));
  });

  onCleanup(() => observer.disconnect());
};

/**
 * Sets up disabled-state watching for the parent-component scenario:
 * observe both parent and parent-group disabled attributes.
 */
const watchParent = (
  host: Element | null,
  isDisabled: { value: boolean },
  onCleanup: (fn: () => void) => void,
): void => {
  isDisabled.value = checkParentDisabled(host);
  if (!host) return;

  const observers: MutationObserver[] = [];
  const update = () => {
    isDisabled.value = checkParentDisabled(host);
  };

  const parent = closestAcrossShadowBoundaries(host, FORM_ASSOCIATED_ELEMENTS);
  if (parent) {
    observers.push(observeDisabled(parent, update));

    const group = closestAcrossShadowBoundaries(parent, "e-radio-group, e-checkbox-group");
    if (group) observers.push(observeDisabled(group, update));
  }

  onCleanup(() => {
    for (const o of observers) o.disconnect();
  });
};

export const useLabel = (options: UseLabelOptions) => {
  const { props } = options;
  const host = useHost();
  const isDisabled = shallowRef(false);

  watchEffect((onCleanup) => {
    if (props.for) {
      watchForProp(props.for, host, isDisabled, onCleanup);
    } else {
      watchParent(host, isDisabled, onCleanup);
    }
  });

  const handleClick = (event: Event) => {
    if (!props.for || !host) return;

    if (isDisabled.value) {
      event.preventDefault();
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    if (isInteractiveTarget(event.target as HTMLElement)) return;

    const element = findAssociatedElement(props.for, host);
    if (element) {
      setTimeout(() => triggerElement(element, props.for, host), 0);
    }
  };

  const isDisabledComputed = computed(() => isDisabled.value);
  const classes = computed(() => mergeClassProp(labelClasses({ disabled: isDisabledComputed.value }), props.class));

  return {
    handleClick,
    isDisabled: isDisabledComputed,
    classes,
  };
};
